import {
  BarChart3,
  Briefcase,
  Building2,
  DollarSign,
  FileText,
  GitBranch,
  Home,
  Landmark,
  MapPin,
  Percent,
  Receipt,
  RefreshCw,
  ShieldCheck,
  Users,
  Users2,
  type LucideIcon,
} from "lucide-react";

import { normalizeSource } from "@/lib/authHelpers";
import type { Source } from "@/types";

/** A single primary-navigation entry rendered in the sidebar. */
export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /** Scopes for which the item is visible when no explicit rule set applies. */
  scope: Source[];
}

/**
 * Canonical primary-navigation model. Order here is the render order in the
 * sidebar. Mirrors the legacy `components/common/Sidebar.tsx` exactly.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: Home,
    href: "/dashboard",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  { id: "clinics", label: "Clinics", icon: Building2, href: "/clinics", scope: ["pglx", "payer"] },
  {
    id: "partners",
    label: "Insurance Partners",
    icon: ShieldCheck,
    href: "/partners",
    scope: ["pglx"],
  },
  {
    id: "employer",
    label: "Employers",
    icon: Briefcase,
    href: "/employers",
    scope: ["pglx", "employer"],
  },
  {
    id: "groups",
    label: "Groups",
    icon: Users2,
    href: "/groups",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "divisions",
    label: "Divisions",
    icon: GitBranch,
    href: "/divisions",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "locations",
    label: "Locations",
    icon: MapPin,
    href: "/locations",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "members",
    label: "Members",
    icon: Users,
    href: "/members",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "hsa-members",
    label: "HSA Members",
    icon: Users,
    href: "/hsa-members",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "member-portal",
    label: "Member Portal",
    icon: Users,
    href: "/member-portal",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "claims",
    label: "Claims",
    icon: FileText,
    href: "/claims",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "payments",
    label: "Payments",
    icon: DollarSign,
    href: "/payments",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "reconcillation",
    label: "Reconciliation",
    icon: RefreshCw,
    href: "/reconcillation",
    scope: ["pglx", "clinic"],
  },
  {
    id: "reports",
    label: "Reports",
    icon: BarChart3,
    href: "/reports",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  { id: "users", label: "User Management", icon: Users, href: "/users", scope: ["pglx"] },
  // Commission / distribution (dev catch-up). pglx-only, mirrors dev's "Commission" group.
  {
    id: "commission-dashboard",
    label: "Commission Dashboard",
    icon: BarChart3,
    href: "/commission-dashboard",
    scope: ["pglx"],
  },
  {
    id: "commission-rules",
    label: "Commission Rules",
    icon: Percent,
    href: "/commission-rules",
    scope: ["pglx"],
  },
  {
    id: "agency-configuration",
    label: "Agencies",
    icon: Building2,
    href: "/agency-configuration",
    scope: ["pglx"],
  },
  {
    id: "agent-configuration",
    label: "Agents",
    icon: Users2,
    href: "/agent-configuration",
    scope: ["pglx"],
  },
  {
    id: "distribution",
    label: "Distribution Splits",
    icon: GitBranch,
    href: "/distribution",
    scope: ["pglx"],
  },
  {
    id: "payout-ledger",
    label: "Payout Ledger",
    icon: DollarSign,
    href: "/payout-ledger",
    scope: ["pglx"],
  },
  // The one entry an agent or agency admin has. Never in the main menu: a platform admin opens an agent's
  // portal from the Agents list, and a portal entry beside the commission screens would lead nowhere useful.
  {
    id: "agent-portal",
    label: "Agent Portal",
    icon: Percent,
    href: "/agent-portal",
    scope: ["pglx", "clinic", "payer", "employer", "group", "division", "location", "member"],
  },
  // "System Settings" (/settings) intentionally absent: no settings page
  // exists in dev either — the legacy link 404'd there and login-bounced
  // here. Restore the item when a real settings screen ships.
  {
    id: "billing-preferences",
    label: "Invoice Preferences",
    icon: Receipt,
    href: "/billing-preferences",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
  {
    id: "billing-accounts",
    label: "Billing Accounts",
    icon: Landmark,
    href: "/billing-accounts",
    scope: ["pglx", "clinic", "payer", "employer"],
  },
];

/** Commission/distribution nav — pglx-only (mirrors dev's "Commission" group). */
const COMMISSION_IDS = [
  "commission-dashboard",
  "commission-rules",
  "agency-configuration",
  "agent-configuration",
  "distribution",
  "payout-ledger",
];

/**
 * Per-scope allow-lists. When a scope has an explicit rule set the sidebar
 * shows exactly those ids (in NAV_ITEMS order); otherwise it falls back to
 * each item's own `scope` list. Mirrors legacy `SIDEBAR_RULES`.
 */
export const SIDEBAR_RULES: Partial<Record<Source, string[]>> = {
  pglx: NAV_ITEMS.map((it) => it.id),
  clinic: [
    "dashboard",
    "clinics",
    "claims",
    "payments",
    "reconcillation",
    "reports",
    "users",
    "settings",
  ],
  payer: NAV_ITEMS.map((it) => it.id).filter(
    (id) => id !== "clinics" && id !== "partners" && !COMMISSION_IDS.includes(id)
  ),
  employer: [
    "dashboard",
    "employer",
    "groups",
    "divisions",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
    "billing-accounts",
  ],
  group: [
    "dashboard",
    "groups",
    "divisions",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  division: [
    "dashboard",
    "divisions",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  location: [
    "dashboard",
    "locations",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
  member: [
    "dashboard",
    "members",
    "hsa-members",
    "claims",
    "payments",
    "reports",
    "billing-preferences",
  ],
};

/** The entity page that administers each hierarchy level. */
const LEVEL_TO_ENTITY_MENU_ID: Record<string, string> = {
  CLIENT: "partners",
  EMPLOYER: "employer",
  GROUP: "groups",
  DIVISION: "divisions",
  LOCATION: "locations",
  MEMBER: "members",
};

/**
 * The entity pages the caller administers, from the backend's `manageableLevels`.
 *
 * `visibleMenuIds` stays THE allowlist, but the two can disagree — and when
 * they do the caller is told in one breath that they administer GROUP..MEMBER
 * and in the next that there is no Groups page. For entity pages take the
 * union: both are backend grants, and the narrower one hides a page the same
 * response says the caller manages. Everything else (claims, payments, reports,
 * billing preferences) obeys `visibleMenuIds` alone, since those are
 * cross-client areas a seated caller genuinely must not open.
 */
export function entityMenuIdsForLevels(manageableLevels?: string[] | null): string[] {
  if (!manageableLevels?.length) return [];
  return manageableLevels
    .map((level) => LEVEL_TO_ENTITY_MENU_ID[String(level).trim().toUpperCase()])
    .filter((id): id is string => !!id);
}

/** What the backend says this caller may open. */
export interface MenuVisibility {
  /** Every navigation id the caller may open, resolved by the backend. */
  visibleMenuIds?: string[] | null;
  /** Hierarchy levels the caller administers, which reach their entity pages. */
  manageableLevels?: string[] | null;
}

/** Whether the backend has answered on visibility yet. */
export function hasBackendMenu(visibility: MenuVisibility): boolean {
  return (visibility.visibleMenuIds?.length ?? 0) > 0;
}

/**
 * Whether the backend grants this menu id. The menu and the route guard must
 * read this the same way, or the sidebar offers an entry that bounces the
 * caller to /unauthorized when they click it.
 */
export function isMenuVisible(id: string, visibility: MenuVisibility): boolean {
  return (
    (visibility.visibleMenuIds?.includes(id) ?? false) ||
    entityMenuIdsForLevels(visibility.manageableLevels).includes(id)
  );
}

/**
 * Routes that stay reachable whatever the allowlist says.
 *
 * The dashboard is where a denied caller is sent, so guarding it would bounce
 * them between the two pages.
 */
/** The agent portal's access is decided by the agent flag (the commission-only shell), not the allowlist. */
export const AGENT_PORTAL_NAV_ID = "agent-portal";

const UNGUARDED_MENU_IDS = new Set(["dashboard", AGENT_PORTAL_NAV_ID]);

/** Longest href first, so a shorter prefix cannot claim a deeper route. */
const ROUTES_BY_SPECIFICITY = [...NAV_ITEMS].sort((a, b) => b.href.length - a.href.length);

/**
 * The menu id that owns `pathname`, or undefined when the route is not
 * menu-gated.
 *
 * Unknown routes are deliberately NOT blocked: a guard that denied by default
 * would lock users out of any page nobody remembered to map.
 */
export function menuIdForPath(pathname: string): string | undefined {
  const match = ROUTES_BY_SPECIFICITY.find(
    ({ href }) => pathname === href || pathname.startsWith(`${href}/`)
  );
  if (!match || UNGUARDED_MENU_IDS.has(match.id)) return undefined;
  return match.id;
}

/**
 * The nav items this caller may open.
 *
 * The backend resolves the whole allowlist from role, seat and agent flag, so
 * when it has answered that decides visibility outright. The scope rules below
 * it are the legacy path, for a session minted before the field existed: they
 * are per-item scope arrays that each admit the caller's OWN level (a
 * group-seated user was offered the Groups page for the node they are pinned
 * to) and hand a location-seated admin the cross-client areas.
 *
 * While visibility is unresolved nothing is returned. Falling back to the
 * permissive rules paints every option and then removes them once the backend
 * answers, which is worse than a rail that fills in a moment later.
 */
export function getVisibleNavItems(
  scope?: Source | string,
  opts?: { isAgent?: boolean; menuVisibilityKnown?: boolean } & MenuVisibility
): NavItem[] {
  const { isAgent, menuVisibilityKnown = true, ...visibility } = opts ?? {};

  // Agents and agency admins work in one place, their portal; nothing else is theirs to open.
  if (isAgent) return NAV_ITEMS.filter((it) => it.id === AGENT_PORTAL_NAV_ID);
  const items = NAV_ITEMS.filter((it) => it.id !== AGENT_PORTAL_NAV_ID);

  if (hasBackendMenu(visibility)) {
    return items.filter((it) => isMenuVisible(it.id, visibility));
  }
  if (!menuVisibilityKnown) return [];

  const normalized = normalizeSource(scope) ?? undefined;
  const enforcedIds = normalized ? SIDEBAR_RULES[normalized] : undefined;

  return items.filter((it) => {
    if (enforcedIds) return enforcedIds.includes(it.id);
    if (normalized) return it.scope.includes(normalized);
    return true;
  });
}
